/**
 * <sticky-atc>
 * Mobile sticky Add to Cart bar. Reuses the page's existing
 * <product-form-component> instead of reimplementing cart logic: it finds the
 * main product form at runtime and calls requestSubmit(), so variant handling,
 * validation, line-item properties, the cart drawer, and analytics all fire
 * exactly as the theme's own buy box does. Shown only when the real buy box is
 * scrolled out of view, on mobile viewports.
 */
if (!customElements.get('sticky-atc')) {
  const MOBILE = '(max-width: 749px)';

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.select = this.querySelector('[data-sticky-variant]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.addBtn = this.querySelector('[data-sticky-add]');
      this.mql = window.matchMedia(MOBILE);

      // Find the MAIN product form (not a quick-add modal): a
      // product-form-component for this product that is not inside a dialog.
      this.formComponent = [...document.querySelectorAll('product-form-component')]
        .find((c) => c.dataset.productId === this.productId && !c.closest('dialog, [hidden]'));
      this.form = this.formComponent?.querySelector('form[data-type="add-to-cart-form"]');
      this.variantInput = this.form?.querySelector('input[name="id"]');
      // Observe the real Add to cart BUTTON so the bar appears only once the
      // shopper has scrolled past it, not while it is still below the fold.
      this.anchor = this.form?.querySelector(
        '.add-to-cart-button, button[name="add"], button[type="submit"]'
      ) || this.formComponent || document.querySelector('main');
      if (!this.form || !this.variantInput || !this.anchor) return; // no buy box, stay hidden

      this.select?.addEventListener('change', () => this.onSelect());
      this.addBtn?.addEventListener('click', () => this.add());
      document.addEventListener('variant:update', (e) => this.onVariantUpdate(e));

      this.observer = new IntersectionObserver(
        (entries) => this.onIntersect(entries[0]),
        { rootMargin: '0px', threshold: 0 }
      );
      this.observer.observe(this.anchor);

      // If the viewport leaves mobile, make sure the bar is hidden.
      this.mql.addEventListener('change', () => { if (!this.mql.matches) this.hide(); });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    onIntersect(entry) {
      if (!this.mql.matches) { this.hide(); return; }
      // Show only after the Add to cart button has scrolled up out of view (the
      // shopper passed it). Never show while it is still below the fold, i.e.
      // not yet reached (bottom edge still below the viewport top).
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      if (scrolledPast) this.show(); else this.hide();
    }

    show() {
      this.hidden = false;
      // allow the element to render before transitioning in
      requestAnimationFrame(() => this.setAttribute('data-visible', ''));
    }

    hide() {
      this.removeAttribute('data-visible');
    }

    onSelect() {
      const opt = this.select.selectedOptions[0];
      if (!opt) return;
      // Keep the real form in sync so both buttons add the same variant.
      if (this.variantInput) this.variantInput.value = opt.value;
      if (this.priceEl && opt.dataset.price) this.priceEl.textContent = opt.dataset.price;
      const available = opt.dataset.available === 'true';
      this.addBtn.disabled = !available;
      this.addBtn.textContent = available ? 'Add to cart' : 'Sold out';
    }

    add() {
      if (this.addBtn.disabled || !this.form) return;
      if (this.select) {
        const opt = this.select.selectedOptions[0];
        if (opt && this.variantInput) this.variantInput.value = opt.value;
      }
      // Reuse the theme's product form: this triggers ProductFormComponent's
      // handleSubmit (AJAX add + cart drawer + events).
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
      else this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }

    // Keep the sticky bar in sync when the main variant picker changes.
    onVariantUpdate(e) {
      const d = e.detail || {};
      if (d.data && d.data.productId && String(d.data.productId) !== String(this.productId)) return;
      const variant = d.resource;
      if (!variant) return;
      if (this.select) {
        const opt = [...this.select.options].find((o) => o.value === String(variant.id));
        if (opt) { this.select.value = opt.value; if (this.priceEl && opt.dataset.price) this.priceEl.textContent = opt.dataset.price; }
      }
      const available = variant.available !== false;
      this.addBtn.disabled = !available;
      this.addBtn.textContent = available ? 'Add to cart' : 'Sold out';
    }
  }

  customElements.define('sticky-atc', StickyAtc);
}
